import { useState, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Upload, Trash2, Link, Image as ImageIcon } from "lucide-react";
import { Card, CardContent } from "@/components/ui/Card.tsx";
import { Button } from "@/components/ui/Button.tsx";
import { Input } from "@/components/ui/Input.tsx";
import { Modal } from "@/components/ui/Modal.tsx";
import { Badge } from "@/components/ui/Badge.tsx";
import { Spinner } from "@/components/ui/Spinner.tsx";
import { assetsApi, type Asset } from "@/api/assets.ts";
import { useToast } from "@/stores/toastStore.ts";

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function isImage(mime: string): boolean {
  return mime.startsWith("image/");
}

export function AssetsPage() {
  const qc = useQueryClient();
  const fileInput = useRef<HTMLInputElement>(null);
  const [showUrlModal, setShowUrlModal] = useState(false);
  const [importUrl, setImportUrl] = useState("");
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);

  const toast = useToast();

  const { data, isLoading } = useQuery({
    queryKey: ["assets"],
    queryFn: () => assetsApi.list(),
    refetchInterval: (query) => {
      // Poll every 5s while any image asset is still being analyzed
      const assets = query.state.data?.assets ?? [];
      const hasPending = assets.some(a => isImage(a.mimeType) && !a.analysis);
      return hasPending ? 5000 : false;
    },
  });

  const uploadFile = useMutation({
    mutationFn: (file: File) => assetsApi.upload(file, undefined, (pct) => setUploadProgress(pct)),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["assets"] });
      if (fileInput.current) fileInput.current.value = "";
      setUploadProgress(null);
      toast.success("Asset uploaded");
    },
    onError: (err) => {
      setUploadProgress(null);
      toast.error(err instanceof Error ? err.message : "Upload failed");
    },
  });

  const uploadUrl = useMutation({
    mutationFn: () => assetsApi.uploadUrl({ url: importUrl }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["assets"] });
      setShowUrlModal(false);
      setImportUrl("");
      toast.success("Asset imported");
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : "Import failed");
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) => assetsApi.delete(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["assets"] });
      setDeleteId(null);
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : "Delete failed");
    },
  });

  const assets: Asset[] = data?.assets ?? [];

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) uploadFile.mutate(file);
  };

  if (isLoading) {
    return (
      <div className="mx-auto max-w-4xl p-8">
        <h1 className="text-xl font-semibold text-surface-900 mb-6">Assets</h1>
        <div className="flex justify-center py-12"><Spinner /></div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-4xl p-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-semibold text-surface-900">Assets</h1>
          <p className="text-sm text-surface-500 mt-1">Upload photos, logos, and product images to use in your videos.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setShowUrlModal(true)}>
            <Link className="w-3.5 h-3.5 mr-1" /> Import URL
          </Button>
          <Button size="sm" onClick={() => fileInput.current?.click()} disabled={uploadFile.isPending}>
            {uploadFile.isPending ? <Spinner className="w-4 h-4" /> : <Upload className="w-3.5 h-3.5 mr-1" />}
            Upload
          </Button>
          <input
            ref={fileInput}
            type="file"
            accept="image/*,audio/*,video/*,.pdf"
            onChange={handleFileSelect}
            className="hidden"
          />
        </div>
      </div>

      {/* Upload progress bar */}
      {uploadProgress !== null && (
        <div className="mb-4">
          <div className="flex items-center justify-between text-xs text-surface-500 mb-1.5">
            <span>Uploading…</span>
            <span>{uploadProgress}%</span>
          </div>
          <div className="w-full h-2 bg-surface-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-indigo-500 rounded-full transition-all duration-200"
              style={{ width: `${uploadProgress}%` }}
            />
          </div>
        </div>
      )}

      {assets.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center">
            <ImageIcon className="w-10 h-10 text-surface-300 mx-auto mb-3" />
            <p className="text-sm font-medium text-surface-700 mb-1">No assets yet</p>
            <p className="text-xs text-surface-400 mb-4">Upload images, logos, or audio to use in your videos.</p>
            <Button size="sm" onClick={() => fileInput.current?.click()}>
              <Upload className="w-3.5 h-3.5 mr-1" /> Upload your first asset
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
          {assets.map((a) => (
            <Card key={a.id} className="group relative overflow-hidden">
              <CardContent className="p-0">
                {isImage(a.mimeType) ? (
                  <img
                    src={a.url}
                    alt={a.filename}
                    className="w-full h-36 object-cover bg-surface-100"
                  />
                ) : (
                  <div className="w-full h-36 bg-surface-100 flex items-center justify-center">
                    <div className="text-center">
                      <Badge variant="default" className="mb-2">{a.mimeType.split("/")[1]?.toUpperCase() ?? "FILE"}</Badge>
                    </div>
                  </div>
                )}
                <div className="p-3">
                  <p className="text-xs font-medium text-surface-800 truncate" title={a.filename}>{a.filename}</p>
                  <p className="text-xs text-surface-400 mt-0.5">{formatBytes(a.sizeBytes)}</p>
                  {a.analysis && (
                    <p className="text-xs text-surface-500 mt-1 line-clamp-2" title={a.analysis.summary}>
                      {a.analysis.summary}
                    </p>
                  )}
                  {a.analysis && (
                    <div className="flex gap-1 mt-1.5 flex-wrap">
                      <Badge variant="default" className="text-[9px] px-1.5 py-0">{a.analysis.category}</Badge>
                      {a.analysis.colors.slice(0, 3).map((c, ci) => (
                        <span key={ci} className="inline-block w-3 h-3 rounded-full border border-surface-200" style={{ background: c }} title={c} />
                      ))}
                    </div>
                  )}
                  {!a.analysis && isImage(a.mimeType) && (
                    <p className="text-[10px] text-surface-400 mt-1 italic">Analyzing…</p>
                  )}
                </div>
                <button
                  aria-label="Delete asset"
                  onClick={() => setDeleteId(a.id)}
                  className="absolute top-2 right-2 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity bg-white/90 backdrop-blur rounded-md p-1.5 shadow-sm border border-surface-200 hover:bg-red-50"
                >
                  <Trash2 className="w-3.5 h-3.5 text-red-500" />
                </button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Import from URL modal */}
      <Modal open={showUrlModal} onClose={() => setShowUrlModal(false)} title="Import from URL" size="sm">
        <div className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-surface-600 mb-1">Image URL</label>
            <Input
              value={importUrl}
              onChange={(e) => setImportUrl(e.target.value)}
              placeholder="https://example.com/photo.jpg"
            />
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-6">
          <Button variant="outline" onClick={() => setShowUrlModal(false)}>Cancel</Button>
          <Button onClick={() => uploadUrl.mutate()} disabled={uploadUrl.isPending || !importUrl.trim()}>
            {uploadUrl.isPending ? <Spinner className="w-4 h-4" /> : "Import"}
          </Button>
        </div>
      </Modal>

      {/* Delete confirmation */}
      <Modal open={deleteId !== null} onClose={() => setDeleteId(null)} title="Delete Asset" size="sm">
        <p className="text-sm text-surface-600 mb-4">Are you sure? This will permanently delete the asset.</p>
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => setDeleteId(null)}>Cancel</Button>
          <Button variant="danger" onClick={() => deleteId && remove.mutate(deleteId)} disabled={remove.isPending}>
            {remove.isPending ? <Spinner className="w-4 h-4" /> : "Delete"}
          </Button>
        </div>
      </Modal>
    </div>
  );
}
